import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { NumberParam, useQueryParam } from 'use-query-params';
import {
    Box,
    Button,
    Card,
    CardHeader,
    Container,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowSelectionModel, } from '@mui/x-data-grid';
import { Add, CurrencyYen, Delete, Event, Schedule, WarningAmber } from '@mui/icons-material';
import NestedMenuItem from 'components/mui/NestedMenuItem';
import Currency from 'components/formaters/Currency';
import { a11yProps } from 'components/mui/Tabs';
import { MemoizedColumnHeaders, MemoizedRow } from 'components/mui/Memoized';
import { isFProject } from 'config/project';
import useContextMenu from 'hooks/useContextMenu';
import { CompanyType } from 'models/CompanyModel';
import { CarModel } from 'models/StockModel';
import { YardModel } from 'models/YardModel';
import { PartyType } from 'models/PartyModel';
import useCarModals from 'modules/cars/hooks/useCarModals';
import AlbumButton from 'modules/cars/common/AlbumButton';
import CarCargoCell from 'modules/cars/common/CarCargoCell';
import CarDocCell from 'modules/cars/common/CarDocCell';
import CarReadinessGauge from 'modules/cars/common/CarReadinessGauge';
import CopyableChassis from 'modules/cars/common/CopyableChassis';
import PartyButton from 'modules/cars/common/PartyButton';
import ProblemMark from 'modules/cars/common/ProblemMark';
import DateLimiter from 'modules/cars/common/DateLimiter';
import VoyageLink from 'modules/cars/common/VoyageLink';
import { calcReadiness } from 'modules/cars/utils/common';
import auth from 'recoil/atoms/auth';


export type CarsViewProps = {
    cars: CarModel[],
    yards: YardModel[],
    yardFilterOptions: {
        value: string | number,
        label: string,
    }[],
    query: string,
    filterInputs: ReactNode[],
    canViewCompany: boolean,
    carCountArrived: number,
    carCountDocs: number,
}

const CarsDesktop = ({
                         cars,
                         yards,
                         yardFilterOptions,
                         query,
                         filterInputs,
                         canViewCompany,
                         carCountArrived,
                         carCountDocs,
                     }: CarsViewProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const modals = useCarModals();
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [yard, setYard] = useQueryParam('yard', NumberParam);

    const {openMenu: openBatch} = useContextMenu(({close, open}) => [
        <MenuItem
            key="add"
            onClick={() => {
                modals.openBatchCarCreateModal();
                close()
            }}
        >
            {t('add_or_update_cars')}
        </MenuItem>,

        my.permissions.has('cars manage') && (
            <NestedMenuItem
                key="yard in"
                parentMenuOpen={open}
                label={t('fill_yard_in')}
                position="left"
            >
                {yards.map(yard => (
                    <MenuItem
                        key={yard.id}
                        onClick={() => {
                            modals.openBatchInYardModal(yard.id, `${yard.port.name} ${yard.name}`);
                            close()
                        }}
                    >
                        {yard.port.name} {yard.name}
                    </MenuItem>
                ))}
            </NestedMenuItem>
        ),

        <NestedMenuItem
            key="exp"
            parentMenuOpen={open}
            label={t('upload_exp_certs')}
            position="left"
        >
            <MenuItem
                onClick={() => {
                    modals.openBatchUploadECModal();
                    close()
                }}
            >
                {t('separated')}
            </MenuItem>
            <MenuItem
                onClick={() => {
                    modals.openBatchUploadECMultiPageModal();
                    close()
                }}
            >
                {t('multi_page')}
            </MenuItem>
        </NestedMenuItem>,

        my.permissions.has('cars update photos') && (
            <MenuItem
                key="photos"
                onClick={() => {
                    modals.openBatchUploadPhotosModal();
                    close()
                }}
            >
                {t('upload_photos')}
            </MenuItem>
        ),

        my.permissions.has('cars manage') && (
            <MenuItem
                key="anshin"
                onClick={() => {
                    modals.confirmAnshin();
                    close()
                }}
            >
                {t('force_anshin')}
            </MenuItem>
        ),
    ]);

    const {openMenu: openYardIn} = useContextMenu(({close}) => yards.map(yard => (
        <MenuItem
            key={yard.id}
            onClick={() => {
                modals.openYardInModal(yard.id, `${yard.port.name} ${yard.name}`);
                close()
            }}
        >
            {yard.port.name} {yard.name}
        </MenuItem>
    )));

    const bulkActions = my.permissions.has('cars update') ? (
        <>
            <span>{t('with_selected')}:</span>
            {' '}
            <Button
                size="small"
                onClick={() => modals.openYardModal(cars.filter(c => selectionModel.includes(c.id)))
                    .then(() => setSelectionModel([]))}
            >
                {t('set_yard')}
            </Button>
            {' '}
            {my.permissions.has('booking make') && (
                <Button
                    size="small"
                    onClick={() => {
                        const selected = cars.filter(c => selectionModel.includes(c.id) && c.yard?.id);
                        if (selected.length)
                            modals.openVoyageModal(selected)
                                .then(() => setSelectionModel([]))
                    }}
                >
                    {t('book_voyage')}
                </Button>
            )}
            {' '}
            {my.permissions.has('booking make') && (
                <Button
                    size="small"
                    onClick={() => {
                        const selected = cars.filter(c => selectionModel.includes(c.id) && c.voyage);
                        if (selected.length)
                            modals.confirmCancelBooking(selected)
                                .then(() => setSelectionModel([]))
                    }}
                >
                    {t('cancel_booking')}
                </Button>
            )}
        </>
    ) : null;

    const mainActions = <>
        {my.permissions.has('cars list export') && (
            <Button
                size="small"
                component={Link}
                href={'/cars/export-list'}
            >
                {t('export_lists')}
            </Button>
        )}
        {' '}
        {my.permissions.has('cars manage') && (
            <Button
                size="small"
                onClick={openYardIn}
            >
                {t('yard_in')}
            </Button>
        )}
        {' '}
        {my.permissions.has('cars update') && (
            <Button
                size="small"
                onClick={openBatch}
            >
                {t('as_batch')}
            </Button>
        )}
        {' '}
        {my.permissions.has('cars create') && (
            <IconButton
                aria-label="add car"
                onClick={modals.openAddCarModal}
            >
                <Add/>
            </IconButton>
        )}
    </>;

    const carsEssentials = my.permissions.has('cars manage essentials');
    const carsCancel = my.permissions.has('cars cancel');

    const carActions = (car: CarModel) => {
        const act = [];

        if (carsEssentials) {
            act.push(
                <GridActionsCellItem
                    icon={<WarningAmber/>}
                    label={t('change_chassis')}
                    onClick={() => modals.openChangeChassisModal(car)}
                    showInMenu
                />
            );
        }

        if (carsCancel) {
            act.push(
                <GridActionsCellItem
                    icon={<Delete/>}
                    label={t('cancel_shipping')}
                    onClick={() => modals.confirmCancelShipping(car)}
                    showInMenu
                />
            );
        }

        return act;
    };

    let hasDamaged = cars.findIndex(car => car.damaged) > -1;

    const columns: GridColDef<CarModel>[] = [
        ...canViewCompany ? [{
            field: 'shipper',
            headerName: t('shipper'),
            valueGetter: params => params.row.company.name,
            flex: 1,
            minWidth: 120,
            cellClassName: () => 'wrapped',
            filterable: false,
        }] as GridColDef<CarModel>[] : [],
        {
            field: 'model',
            headerName: t('model'),
            renderCell: params => <span>
                {params.row.make} <ProblemMark car={params.row}/><br/>
                {params.row.model}
            </span>,
            flex: 1,
            minWidth: 130,
        },
        {
            field: 'chassisNumber',
            headerName: t('chassis'),
            renderCell: params => <CopyableChassis car={params.row} query={query}/>,
            width: 210,
        },
        {
            field: 'photos',
            headerName: t('photos'),
            type: 'number',
            valueGetter: params => params.row.photoCount,
            renderCell: params => <AlbumButton car={params.row}/>,
            width: 140,
            filterable: false,
        },
        {
            field: 'unitPrice',
            headerName: t('price'),
            type: 'number',
            width: 60,
            renderCell: params => <Tooltip title={<Currency value={params.value} />} placement="top">
                <CurrencyYen
                    color={!params.value ? 'error' : (params.value >= 4000000 ? 'warning' : undefined)}
                    fontSize="small"
                />
            </Tooltip>
        },
        {
            field: 'yard_id',
            headerName: t('yard'),
            valueGetter: params => params.row.yard?.id,
            renderCell: params => params.row.yard && (
                <>
                    <Box>
                        {params.row.yard.port}<br/>
                        {params.row.yard.name}
                    </Box>
                    {!params.row.yard.arrived && (
                        <Tooltip title={t('in_transit')} placement="top">
                            <Schedule
                                color="error"
                                fontSize="small"
                                onClick={() => modals.openYardInSingleModal(params.row)}
                            />
                        </Tooltip>
                    )}
                </>
            ),
            filterable: false,
            flex: 1,
            minWidth: 135,
        },
        {
            field: 'voyage',
            headerName: t('voyage'),
            valueGetter: params => params.row.voyage?.id || 0,
            renderCell: params => params.row.voyage ? <>
                <VoyageLink voyage={params.row.voyage}/>
                {' '}
                {params.row.shipDateLimit && (
                    <Tooltip
                        title={<DateLimiter limit={params.row.shipDateLimit}/>}
                        placement="top"
                    >
                        <Event
                            fontSize="small"
                            color="warning"
                            sx={{verticalAlign: 'middle'}}
                        />
                    </Tooltip>
                )}
            </> : (params.row.shipDateLimit ?
                <Box sx={{color: params.row.shipDateLimit.after ? 'error.light' : 'success.light'}}>
                    {params.row.shipDateLimit.date === '3000-01-01' ?
                        <>{t('temporary_stop')}</> :
                        <DateLimiter limit={params.row.shipDateLimit} br/>
                    }
                </Box> :
                null),
            width: 130,
            filterable: false,
        },
        ...my.type !== CompanyType.PortAgent && !isFProject ? [
            {
                field: 'consignee',
                headerName: t('consignee'),
                type: 'number',
                valueGetter: params => params.row.parties.consignee?.id || 0,
                width: 115,
                filterable: false,
                renderCell: params => <PartyButton
                    car={params.row}
                    party={params.row.parties.consignee}
                    type={PartyType.Consignee}
                />,
                align: 'left',
                cellClassName: () => 'wrapped',
            },
            {
                field: 'notify',
                headerName: t('notify'),
                type: 'number',
                valueGetter: params => params.row.parties.notify?.id || 0,
                width: 115,
                filterable: false,
                renderCell: params => <PartyButton
                    car={params.row}
                    party={params.row.parties.notify}
                    type={PartyType.Notify}
                />,
                align: 'left',
                cellClassName: () => 'wrapped',
            },
            ...my.type !== CompanyType.VesselOwner ? [
            {
                field: 'dealer',
                headerName: t('dealer'),
                type: 'number',
                valueGetter: params => params.row.parties.dealer?.id || 0,
                width: 115,
                filterable: false,
                renderCell: params => <PartyButton
                    car={params.row}
                    party={params.row.parties.dealer}
                    type={PartyType.Dealer}
                />,
                align: 'left',
                cellClassName: () => 'wrapped',
            }] as GridColDef<CarModel>[] : [],
        ] as GridColDef<CarModel>[] : [],
        {
            field: 'exportCertificate',
            headerName: t('docs'),
            width: hasDamaged ? 120 : 70,
            filterable: false,
            type: 'number',
            valueGetter: params => Number(Boolean(params.row.documents.exportCertificate || params.row.documents.vehicleInvoice)),
            renderCell: params => <CarDocCell
                car={params.row}
            />
        },
        {
            field: 'innerCargo',
            headerName: t('cargo'),
            width: 70,
            filterable: false,
            type: 'number',
            valueGetter: params => params.row.innerCargo.length,
            renderCell: params => <CarCargoCell
                car={params.row}
                openInnerCargoModal={modals.openInnerCargoModal}
            />
        },
        {
            field: 'filled',
            headerName: t('filled'),
            valueGetter: params => calcReadiness(params.row),
            renderCell: params => <CarReadinessGauge car={params.row}/>,
            width: 60,
            filterable: false,
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => carActions(params.row),
            width: 50,
        },
    ];

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Tabs
                variant="scrollable"
                value={yard === undefined ? '' : yard}
                onChange={(_, v) => setYard(v === '' ? undefined : Number(v))}
                aria-label="Yards"
            >
                {yardFilterOptions.map(y => (
                    <Tab
                        key={y.value}
                        value={y.value}
                        label={y.label}
                        {...a11yProps('yard', y.value)}
                    />
                ))}
            </Tabs>

            <Grid container spacing={2} sx={{mb: 2}}>
                {filterInputs.map((children, i) => (
                    <Grid key={i} item xs={12} sm={2}>
                        {children}
                    </Grid>
                ))}
            </Grid>

            <Card>
                <CardHeader
                    title={`${t('cars_pending')} (${cars.length}), ${t('on_yard')} (${carCountArrived}), ${t('docs')} (${carCountDocs})`}
                    action={selectionModel.length ? bulkActions : mainActions}
                />

                <Box sx={{height: 'calc(100vh - 264px)', padding: '0 1rem'}}>
                    <DataGrid
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        rows={cars}
                        columns={columns}
                        sx={{
                            '& .MuiDataGrid-cell.wrapped': {
                                whiteSpace: 'normal !important',
                                wordWrap: 'break-word !important',
                            }
                        }}
                        slots={{
                            row: MemoizedRow,
                            columnHeaders: MemoizedColumnHeaders,
                        }}
                    />
                </Box>
            </Card>
        </Container>
    )
};

export default CarsDesktop;
